import { apiClient } from './client'
import { API_URL } from '@/lib/utils/constants'
import type {
  Family,
  FamilyDetail,
  FamilyMember,
  Invitation,
  InvitationDetail,
  CreateFamilyRequest,
  InviteMemberRequest,
  UpdateMemberRequest,
  FamilySettings,
} from '@/types'

// Paginated response type from Django REST Framework
interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// API returns user_role, but frontend expects role
interface FamilyApiResponse {
  id: string
  name: string
  user_role: string
  user_permissions: Family["permissions"]
  member_count: number
  created_at: string
}

export const familyApi = {
  // Family CRUD
  list: async (): Promise<Family[]> => {
    const response = await apiClient.get<PaginatedResponse<FamilyApiResponse>>('/families')
    // Map user_role to role for frontend compatibility
    return response.results.map((f) => ({
      id: f.id,
      name: f.name,
      role: f.user_role as Family['role'],
      permissions: f.user_permissions,
      member_count: f.member_count,
      created_at: f.created_at,
    }))
  },

  get: (familyId: string) => apiClient.get<FamilyDetail>(`/families/${familyId}`),

  create: (data: CreateFamilyRequest) => apiClient.post<FamilyDetail>('/families', data),

  update: (familyId: string, data: Partial<CreateFamilyRequest>) =>
    apiClient.patch<FamilyDetail>(`/families/${familyId}`, data),

  delete: (familyId: string) => apiClient.delete<void>(`/families/${familyId}`),

  // Family settings
  updateSettings: (familyId: string, settings: Partial<FamilySettings>) =>
    apiClient.patch<FamilyDetail>(`/families/${familyId}/settings`, settings),

  // Members
  getMembers: async (familyId: string) => {
    const members = await apiClient.get<Array<Omit<FamilyMember, 'permissions'> & { permissions: Record<string, boolean> }>>(`/families/${familyId}/members`)
    return members.map((member) => ({ ...member, permissions: Object.entries(member.permissions)
      .filter(([, allowed]) => allowed).map(([name]) => name) as FamilyMember['permissions'] }))
  },

  getMember: async (familyId: string, memberId: string) => {
    const member = await apiClient.get<Omit<FamilyMember, 'permissions'> & { permissions: Record<string, boolean> }>(`/families/${familyId}/members/${memberId}`)
    return { ...member, permissions: Object.entries(member.permissions)
      .filter(([, allowed]) => allowed).map(([name]) => name) as FamilyMember['permissions'] }
  },

  updateMember: (familyId: string, memberId: string, data: UpdateMemberRequest) =>
    apiClient.put<FamilyMember>(`/families/${familyId}/members/${memberId}`, data),

  removeMember: (familyId: string, memberId: string) =>
    apiClient.delete<void>(`/families/${familyId}/members/${memberId}`),

  leaveFamily: (familyId: string) =>
    apiClient.post<void>(`/families/${familyId}/leave`),

  // Invitations
  getInvitations: (familyId: string) =>
    apiClient.get<Invitation[]>(`/families/${familyId}/invitations`),

  inviteMember: (familyId: string, data: InviteMemberRequest) =>
    apiClient.post<Invitation>(`/families/${familyId}/invite`, data),

  cancelInvitation: (familyId: string, invitationId: string) =>
    apiClient.delete<void>(`/families/${familyId}/invitations/${invitationId}`),

  resendInvitation: (familyId: string, invitationId: string) =>
    apiClient.post<Invitation>(`/families/${familyId}/invitations/${invitationId}/resend`),

  // Pending invitations for current user
  getPendingInvitations: () =>
    apiClient.get<Invitation[]>('/invitations'),

  acceptInvitation: (invitationId: string) =>
    apiClient.post<FamilyMember>(`/invitations/${invitationId}/accept`),

  declineInvitation: (invitationId: string) =>
    apiClient.post<void>(`/invitations/${invitationId}/decline`),

  // Public invitation details (no auth required)
  getInvitationDetails: async (invitationId: string): Promise<InvitationDetail> => {
    const url = `${API_URL}/invitations/${invitationId}`
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Invitation not found' } }))
      throw new Error(errorData.error?.message || 'Failed to fetch invitation')
    }
    return response.json()
  },
}
